// A API nova usa nomes de campo em ingles e devolve listagem paginada no formato
// { totalRows, totalPages, data }. O admin continua em portugues: a traducao entre
// os dois mundos acontece aqui, para que os models e os templates nao mudem.

export interface FieldMap {
    [portugues: string]: string;
}

/** Converte um objeto do admin (pt) para o corpo que a API espera (en). */
export function paraApi(obj: any, map: FieldMap): any {

    const out: any = {};

    if (!obj) {
        return out;
    }

    if (obj.id) {
        out.id = obj.id;
    }

    Object.keys(map).forEach(pt => {
        if (obj[pt] !== undefined) {
            out[map[pt]] = obj[pt];
        }
    });

    return out;
}

/** Converte um registro devolvido pela API (en) para o formato do admin (pt). */
export function daApi(obj: any, map: FieldMap): any {

    if (!obj) {
        return obj;
    }

    const out: any = { id: obj.id };

    Object.keys(map).forEach(pt => {
        out[pt] = obj[map[pt]];
    });

    return out;
}

/** Query string de listagem no formato da API nova. */
export function parametrosLista(
    numeroPagina: number,
    registroPorPagina: number,
    ordenacao: string,
    sentidoOrdenacao: string,
    filtro: string,
    ordenacaoMap: FieldMap,
    ordenacaoPadrao: string
): string {

    // as tabelas do admin usam o indice da coluna como chave de ordenacao ("1", "2", ...)
    const orderColumn = (ordenacao && ordenacaoMap[ordenacao]) ? ordenacaoMap[ordenacao] : ordenacaoPadrao;
    const orderDirection = String(sentidoOrdenacao || 'asc').toLowerCase() === 'desc' ? 'desc' : 'asc';

    let query = `?page=${numeroPagina}&count=${registroPorPagina}&orderColumn=${orderColumn}&orderDirection=${orderDirection}`;

    if (filtro) {
        query += `&filter=${encodeURIComponent(filtro)}`;
    }

    return query;
}

/** Desembrulha { totalRows, totalPages, data } devolvendo os itens ja traduzidos. */
export function listaDaApi(res: any, map: FieldMap): { itens: any[], numeroPaginas: number, totalRegistros: number } {

    const brutos = (res && res.data) ? res.data : [];

    return {
        itens: brutos.map(item => daApi(item, map)),
        numeroPaginas: (res && res.totalPages) ? res.totalPages : 1,
        totalRegistros: (res && res.totalRows) ? res.totalRows : brutos.length
    };
}

// ---------------------------------------------------------------------------
// Mapas de campo por entidade (chave = admin, valor = API)
// ---------------------------------------------------------------------------

export const MAP_BANNER: FieldMap = {
    titulo: 'title',
    descricao: 'description',
    link: 'link',
    imagem: 'image'
};

export const MAP_BENEFICIO: FieldMap = {
    icone: 'icon',
    nome: 'name',
    ordem: 'order'
};

export const MAP_CURSO: FieldMap = {
    nome: 'name',
    descricaoSimples: 'simpleDescription',
    descricao: 'description',
    situacao: 'situation',
    slug: 'slug',
    tag: 'tag',
    data: 'date',
    valor: 'price',
    valorPromocao: 'promotionalPrice',
    imagemPequena: 'smallImage',
    imagemGrande: 'largeImage'
};

export const MAP_DEPOIMENTO: FieldMap = {
    foto: 'photo',
    nome: 'name',
    descritivo: 'descriptive',
    depoimento: 'testimonial',
    data: 'date'
};

export const MAP_FATO: FieldMap = {
    imagem: 'image',
    fato1: 'fact1', valorFato1: 'valueFact1',
    fato2: 'fact2', valorFato2: 'valueFact2',
    fato3: 'fact3', valorFato3: 'valueFact3',
    fato4: 'fact4', valorFato4: 'valueFact4'
};

export const MAP_NOTICIA: FieldMap = {
    titulo: 'title',
    descricaoSimples: 'simpleDescription',
    descricao: 'description',
    data: 'date',
    tempoLeitura: 'readingTime',
    situacao: 'situation',
    slug: 'slug',
    tag: 'tag',
    imagemPequena: 'smallImage',
    imagemGrande: 'largeImage'
};

export const MAP_SOBRE: FieldMap = {
    descricao: 'description',
    imagem: 'image'
};

export const MAP_CADASTRO_GERAL: FieldMap = {
    titulo: 'title',
    descricao: 'description',
    palavrasChave: 'keywords',
    cor: 'color'
};

export const MAP_CONTATO_GERAL: FieldMap = {
    telefoneSecretaria: 'phoneSecretary',
    telefoneComercial: 'phoneCommercial',
    telefoneFixo: 'landline',
    whatsApp: 'whatsapp',
    email: 'email',
    linkFacebook: 'facebook',
    linkInstagram: 'instagram',
    linkLinkedin: 'linkedin',
    uf: 'state',
    cidade: 'city',
    rua: 'address',
    numeroEndereco: 'addressNumber',
    bairro: 'district',
    cep: 'zipCode',
    complemento: 'addressComplement',
    textoModal: 'modalText'
};

export const MAP_MENSAGEM: FieldMap = {
    nome: 'name',
    email: 'email',
    telefone: 'phone',
    assunto: 'subject',
    estilo: 'type',
    mensagem: 'text',
    data: 'date',
    arquivo: 'file'
};

export const MAP_USUARIO: FieldMap = {
    nome: 'name',
    sobrenome: 'lastName',
    email: 'email',
    senha: 'password',
    situacao: 'situation',
    solicitaAlteracao: 'requestChange',
    codigoRecuperacao: 'recoveryCode'
};

// ---------------------------------------------------------------------------
// Ordenacao: indice da coluna na tabela do admin -> campo da API
// ---------------------------------------------------------------------------

export const ORDEM_BENEFICIO: FieldMap = { '1': 'name', icone: 'icon' };
export const ORDEM_CURSO: FieldMap = { '1': 'name', '2': 'simpleDescription', '3': 'date', '4': 'situation' };
export const ORDEM_DEPOIMENTO: FieldMap = { '1': 'name', '2': 'descriptive', '3': 'testimonial', '4': 'date' };
export const ORDEM_NOTICIA: FieldMap = { '1': 'title', '2': 'simpleDescription', '3': 'date', '4': 'situation' };
export const ORDEM_USUARIO: FieldMap = { '1': 'name', '2': 'email', '3': 'situation' };
export const ORDEM_MENSAGEM: FieldMap = { '1': 'name', '2': 'email', '3': 'phone', '4': 'type', '5': 'text' };
